/**
 * Month-grid leave calendar — Approved + Pending chips.
 */
import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { clsx } from 'clsx';
import { Button } from '../../../shell/Button.js';
import { Badge } from '../../../shell/Badge.js';
import {
  calendarGrid,
  isoDateInRange,
  toIsoDate,
  utcTodayIso,
} from '@shared/util/dates.js';
import type { TimeOffRequest } from '@shared/types';
import { formatDate } from '../../../lib/format.js';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

type LeaveCalendarProps = {
  requests: TimeOffRequest[];
  employeeLabel: Map<string, string>;
  focusedDay?: string | null;
  onFocusDay?: (day: string | null) => void;
  onSelectRequest?: (request: TimeOffRequest) => void;
  /** When false, only Approved chips are shown (default true = Approved + Pending). */
  includePending?: boolean;
};

function shortName(label: string): string {
  const parts = label.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'Employee';
  if (parts.length === 1) return parts[0]!.slice(0, 12);
  return `${parts[0]} ${parts[parts.length - 1]![0]}.`.slice(0, 14);
}

function partialTimeLabel(request: TimeOffRequest): string {
  return request.duration === 'PartialDay' && request.startTime && request.endTime
    ? ` · ${request.startTime}–${request.endTime}`
    : '';
}

export function LeaveCalendar({
  requests,
  employeeLabel,
  focusedDay = null,
  onFocusDay,
  onSelectRequest,
  includePending = true,
}: LeaveCalendarProps) {
  const today = utcTodayIso();
  const [cursor, setCursor] = useState(() => {
    const base = focusedDay ?? today;
    const [y, m] = base.split('-').map(Number);
    return { year: y!, monthIndex: m! - 1 };
  });

  const visible = useMemo(
    () =>
      requests.filter((r) =>
        r.status === 'Approved' || (includePending && r.status === 'Pending'),
      ),
    [requests, includePending],
  );

  const cells = useMemo(
    () => calendarGrid(cursor.year, cursor.monthIndex),
    [cursor.year, cursor.monthIndex],
  );

  const byDay = useMemo(() => {
    const map = new Map<string, TimeOffRequest[]>();
    for (const cell of cells) {
      const dayRequests = visible.filter((r) => isoDateInRange(cell.date, r.from, r.to));
      if (dayRequests.length > 0) map.set(cell.date, dayRequests);
    }
    return map;
  }, [cells, visible]);

  const monthLabel = new Date(Date.UTC(cursor.year, cursor.monthIndex, 1)).toLocaleDateString(
    'en-US',
    { month: 'long', year: 'numeric', timeZone: 'UTC' },
  );

  const shiftMonth = (delta: number) => {
    setCursor((c) => {
      const dt = new Date(Date.UTC(c.year, c.monthIndex + delta, 1));
      return { year: dt.getUTCFullYear(), monthIndex: dt.getUTCMonth() };
    });
  };

  const goToday = () => {
    const [y, m] = today.split('-').map(Number);
    setCursor({ year: y!, monthIndex: m! - 1 });
    onFocusDay?.(today);
  };

  const focusedRequests = focusedDay ? (byDay.get(focusedDay) ?? []) : [];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" aria-label="Previous month" onClick={() => shiftMonth(-1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <h2 className="min-w-[10rem] text-center text-sm font-semibold text-slate-900 dark:text-slate-100">
            {monthLabel}
          </h2>
          <Button size="sm" variant="ghost" aria-label="Next month" onClick={() => shiftMonth(1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <Button size="sm" variant="secondary" onClick={goToday}>
          Today
        </Button>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700">
        <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/60">
          {WEEKDAYS.map((d) => (
            <div
              key={d}
              className="px-2 py-2 text-center text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400"
            >
              {d}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 auto-rows-fr">
          {cells.map((cell) => {
            const dayRequests = byDay.get(cell.date) ?? [];
            const isToday = cell.date === today;
            const isFocused = cell.date === focusedDay;

            return (
              <button
                key={cell.date}
                type="button"
                onClick={() => onFocusDay?.(isFocused ? null : cell.date)}
                title={
                  dayRequests.length > 0
                    ? dayRequests
                        .map((r) => `${employeeLabel.get(r.employeeId) ?? 'Employee'}${partialTimeLabel(r)} (${r.status})`)
                        .join('\n')
                    : undefined
                }
                className={clsx(
                  'min-h-[7.5rem] border-b border-r border-slate-100 p-1.5 text-left align-top transition-colors dark:border-slate-800',
                  'hover:bg-slate-50 dark:hover:bg-slate-800/50',
                  !cell.inMonth && 'bg-slate-50/70 text-slate-400 dark:bg-slate-900/40 dark:text-slate-600',
                  isFocused && 'ring-2 ring-inset ring-sky-500',
                )}
              >
                <div className="mb-1 flex items-center justify-between gap-1">
                  <span
                    className={clsx(
                      'inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium',
                      isToday && 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900',
                      !isToday && cell.inMonth && 'text-slate-700 dark:text-slate-300',
                    )}
                  >
                    {Number(cell.date.slice(8, 10))}
                  </span>
                </div>

                <div className="space-y-0.5">
                  {dayRequests.slice(0, 3).map((r) => {
                    const label = employeeLabel.get(r.employeeId) ?? 'Employee';
                    return (
                      <span
                        key={r.id}
                        role={onSelectRequest ? 'button' : undefined}
                        tabIndex={onSelectRequest ? 0 : undefined}
                        onClick={(e) => {
                          if (!onSelectRequest) return;
                          e.stopPropagation();
                          onSelectRequest(r);
                        }}
                        onKeyDown={(e) => {
                          if (!onSelectRequest) return;
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.stopPropagation();
                            onSelectRequest(r);
                          }
                        }}
                        className={clsx(
                          'block truncate rounded px-1 py-0.5 text-[10px] font-medium leading-tight',
                          r.status === 'Approved'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                            : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
                          onSelectRequest && 'cursor-pointer hover:opacity-80',
                        )}
                      >
                        {shortName(label)}
                      </span>
                    );
                  })}
                  {dayRequests.length > 3 && (
                    <span className="block px-1 text-[10px] text-slate-500 dark:text-slate-400">
                      +{dayRequests.length - 3} more
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {focusedDay && (
        <div className="rounded-xl border border-slate-200 p-3 dark:border-slate-700">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
              {formatDate(focusedDay)}
            </p>
            <Button size="sm" variant="ghost" onClick={() => onFocusDay?.(null)}>
              Clear
            </Button>
          </div>
          {focusedRequests.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {includePending ? 'No approved or pending leave.' : 'No approved leave.'}
            </p>
          ) : (
            <ul className="space-y-2">
              {focusedRequests.map((r) => {
                const label = employeeLabel.get(r.employeeId) ?? 'Employee';
                return (
                  <li key={r.id}>
                    <button
                      type="button"
                      className="flex w-full items-center justify-between gap-3 rounded-lg px-2 py-1.5 text-left hover:bg-slate-50 dark:hover:bg-slate-800/60"
                      onClick={() => onSelectRequest?.(r)}
                    >
                      <div>
                        <p className="text-sm text-slate-800 dark:text-slate-200">{label}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          {r.type === 'UTO' ? 'UTO (Unpaid)' : r.type} · {formatDate(toIsoDate(r.from))} – {formatDate(toIsoDate(r.to))}
                          {partialTimeLabel(r)}
                          {r.reason ? ` · ${r.reason}` : ''}
                        </p>
                      </div>
                      <Badge tone={r.status === 'Approved' ? 'green' : 'yellow'}>{r.status}</Badge>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
